import type { SpreadsheetTable, SpreadsheetRow } from "./spreadsheet.js";
import { normalizeMeasurement, FIELD_DIMENSION } from "./unit-normalization.js";

/**
 * IMPORTAÇÃO INTELIGENTE DE SISTEMAS E TUBULAÇÕES (Bloco 04) — regras de
 * leitura e crítica, puras e sem dependência de serviço/repositório.
 *
 * Princípios:
 * - Nenhum dado é inventado. Uma coluna ausente vira campo nulo, nunca um
 *   valor plausível. A unidade de medida é lida do próprio cabeçalho
 *   ("Pressão de projeto (kgf/cm²)"), não presumida.
 * - O cabeçalho é reconhecido por sinônimos normalizados (sem acento, sem
 *   caixa), porque a planilha do cliente raramente repete o rótulo exato.
 * - Problema de ESTRUTURA (TAG faltando, TAG repetida, número inválido,
 *   Sistema inexistente) é ERRO e bloqueia a linha. Valor fora do domínio
 *   esperado (Classe de fluido diferente de A-D, por exemplo) é AVISO e
 *   não bloqueia: a planilha é do cliente e o sistema não deve descartar
 *   cadastro legítimo por causa de um rótulo divergente.
 */

export type ImportSeverity = "ERROR" | "WARNING";

export interface ImportIssue {
  /** Linha real na planilha (1-based), para o usuário achar o problema no arquivo. */
  readonly rowNumber: number;
  readonly column: string | null;
  readonly severity: ImportSeverity;
  readonly message: string;
}

export interface MeasurementDraft {
  /** Valor já normalizado para a unidade canônica da grandeza. */
  readonly value: number;
  readonly unit: string;
  /** O que veio no arquivo, preservado para auditoria. */
  readonly originalValue: number;
  readonly originalUnit: string;
  /** false quando a unidade do arquivo não foi reconhecida. */
  readonly recognized: boolean;
}

/** Campos mapeados de uma linha — subconjunto dos campos reais de `Equipment`. */
export interface ImportedEquipmentDraft {
  readonly rowNumber: number;
  readonly kind: "PIPING_SYSTEM" | "PIPING_LINE";
  readonly tag: string;
  readonly name: string;
  /** TAG do Sistema de Tubulação ao qual a linha pertence (somente PIPING_LINE). */
  readonly systemTag: string | null;
  /** SERVIÇO — coluna própria (Serviço/Descrição), distinta de FLUIDO mesmo quando o arquivo só tem uma coluna combinada. */
  readonly service: string | null;
  /** FLUIDO — coluna própria (Fluido/Produto) quando existir no arquivo; cai para SERVIÇO só se não houver coluna de Fluido dedicada. */
  readonly fluid: string | null;
  /** CÓDIGO DO FLUIDO — coluna própria (ex.: "Cód. fluido"), nunca combinada com `fluid`: os dois são preservados quando o arquivo os separa (comando "correção da importação", item 5). */
  readonly fluidCode: string | null;
  readonly fluidClass: string | null;
  readonly nr13Category: string | null;
  readonly designPressure: MeasurementDraft | null;
  readonly operatingPressure: MeasurementDraft | null;
  readonly designTemperature: MeasurementDraft | null;
  readonly operatingTemperature: MeasurementDraft | null;
  readonly pmtaValue: number | null;
  readonly pmtaUnit: string | null;
  readonly pressureClass: string | null;
  readonly spec: string | null;
  readonly material: string | null;
  /** DESENHO (de Referência) — coluna própria; cai para a coluna combinada legada só se não houver "Desenho" dedicado. */
  readonly drawingReference: string | null;
  /** FLUXOGRAMA (P&ID/PFD) — coluna própria; cai para a coluna combinada legada só se não houver "Fluxograma" dedicado. */
  readonly flowDiagramReference: string | null;
  readonly lineNumber: string | null;
  readonly fromReference: string | null;
  readonly toReference: string | null;
  readonly nps: string | null;
  readonly schedule: string | null;
  readonly nominalThickness: MeasurementDraft | null;
  readonly minimumThickness: MeasurementDraft | null;
  /**
   * CADASTRO TÉCNICO DA LINHA.
   *
   * Todos têm campo correspondente em `Equipment` (`outsideDiameter`,
   * `corrosionAllowance`, `materialFamily`, `fabricationStandard`,
   * `dimensionalStandard`) e vinham na planilha, mas caíam em `extras` — a
   * Lista de Linhas criava o TAG e o resto era redigitado ativo por ativo.
   * Opcionais porque a aba de Sistemas não os traz.
   */
  readonly outsideDiameter?: MeasurementDraft | null;
  readonly corrosionAllowance?: MeasurementDraft | null;
  readonly materialFamily?: string | null;
  readonly fabricationStandard?: string | null;
  readonly dimensionalStandard?: string | null;
  /** Colunas sem campo de primeira classe em Equipment, preservadas sem perda. */
  readonly extras: Readonly<Record<string, string>>;
}

export interface SheetMappingResult {
  readonly drafts: readonly ImportedEquipmentDraft[];
  readonly issues: readonly ImportIssue[];
  /** Cabeçalhos esperados que não existem na planilha — informativo, não bloqueia. */
  readonly missingColumns: readonly string[];
  /** Cabeçalhos presentes que o sistema não conhece — preservados em `extras`. */
  readonly unmappedColumns: readonly string[];
}

// ------------------------------------------------------------- normalização

export function normalizeHeader(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    // Expoentes viram o dígito equivalente: sem isto, "(kgf/cm²)" digitado
    // pelo cliente como "(kgf/cm2)" deixaria de casar com a coluna.
    .replace(/[²⁲]/g, "2")
    .replace(/[³⁳]/g, "3")
    .replace(/[^a-zA-Z0-9]+/g, " ")
    .trim()
    .toLowerCase();
}

/** Aceita "18", "18.5" e "18,5"; recusa texto. */
export function parseDecimal(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === "") return null;
  const normalized = trimmed.replace(/\s/g, "").replace(",", ".");
  if (!/^-?\d+(\.\d+)?$/.test(normalized)) return null;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

/** Extrai a unidade declarada no próprio cabeçalho: "PMTA (kgf/cm²)" -> "kgf/cm²". */
export function unitFromHeader(header: string, fallback: string): string {
  const match = /\(([^)]+)\)/.exec(header);
  const unit = match?.[1]?.trim();
  return unit && unit !== "" ? unit : fallback;
}

interface ColumnSpec {
  readonly key: string;
  readonly aliases: readonly string[];
  readonly required?: boolean;
  readonly defaultUnit?: string;
  /**
   * Só casa por título EXATO, nunca pelo índice sem unidade.
   *
   * Existe por causa de "DE": na planilha real ele é a ORIGEM da linha no
   * bloco de processo e o DIÂMETRO EXTERNO ("DE (mm)") no bloco de
   * materiais. Os dois reduzem a "de" quando a unidade é removida, e sem
   * esta trava a origem da linha passaria a receber o diâmetro — um erro
   * que ninguém perceberia lendo a tela, porque ambos são texto curto.
   */
  readonly exactOnly?: boolean;
}

/**
 * CONFIGURAÇÕES → IMPORTAÇÕES. Aliases EXPLÍCITOS (título exato, depois de
 * normalizado — nunca aproximação) e marcadores de vazio ACRESCENTADOS aos
 * que já valem. Sem opções, o mapeamento é exatamente o de sempre.
 */
export interface ImportMappingOptions {
  readonly extraAliases?: ReadonlyArray<{ readonly field: string; readonly alias: string }>;
  readonly extraEmptyMarkers?: readonly string[];
}

function withExtraAliases(specs: readonly ColumnSpec[], extra: ImportMappingOptions["extraAliases"]): readonly ColumnSpec[] {
  if (!extra || extra.length === 0) return specs;
  return specs.map(spec => {
    const more = extra.filter(a => a.field === spec.key).map(a => a.alias.trim()).filter(a => a !== "");
    return more.length === 0 ? spec : { ...spec, aliases: [...spec.aliases, ...more] };
  });
}

/** Resolve rótulo real da planilha para cada campo conhecido. */
class ColumnResolver {
  private readonly byKey = new Map<string, string>();
  readonly missing: string[] = [];
  readonly unmapped: string[] = [];
  private readonly extraEmpty: ReadonlySet<string>;

  constructor(headers: readonly string[], baseSpecs: readonly ColumnSpec[], options: ImportMappingOptions = {}) {
    const specs = withExtraAliases(baseSpecs, options.extraAliases);
    this.extraEmpty = new Set((options.extraEmptyMarkers ?? []).map(normalizeMarker).filter(m => m !== ""));
    const normalizedHeaders = new Map<string, string>();
    // Índice adicional sem a unidade entre parênteses: o cliente pode
    // escrever "Pressão de projeto (bar)" em vez de "(kgf/cm²)". A coluna é
    // a mesma; a unidade é lida do cabeçalho e normalizada depois.
    const withoutUnit = new Map<string, string>();
    const stripUnit = (value: string) => normalizeHeader(value.replace(/\([^)]*\)/g, " "));
    for (const header of headers) {
      if (!header) continue;
      normalizedHeaders.set(normalizeHeader(header), header);
      const bare = stripUnit(header);
      if (bare !== "" && !withoutUnit.has(bare)) withoutUnit.set(bare, header);
    }
    const consumed = new Set<string>();
    for (const spec of specs) {
      let found: string | undefined;
      for (const alias of spec.aliases) {
        const candidate = normalizedHeaders.get(normalizeHeader(alias));
        if (candidate) { found = candidate; break; }
      }
      if (!found && !spec.exactOnly) {
        for (const alias of spec.aliases) {
          const candidate = withoutUnit.get(stripUnit(alias));
          if (candidate && !consumed.has(candidate)) { found = candidate; break; }
        }
      }
      if (found) {
        this.byKey.set(spec.key, found);
        consumed.add(found);
      } else {
        this.missing.push(spec.aliases[0] ?? spec.key);
      }
    }
    for (const header of headers) {
      if (header && !consumed.has(header)) this.unmapped.push(header);
    }
  }

  header(key: string): string | null {
    return this.byKey.get(key) ?? null;
  }

  text(row: SpreadsheetRow, key: string): string {
    const header = this.byKey.get(key);
    if (!header) return "";
    const value = (row.values[header] ?? "").trim();
    return this.extraEmpty.size > 0 && this.extraEmpty.has(normalizeMarker(value)) ? "" : value;
  }
}

// ------------------------------------------------------------------ colunas

// FLUIDO e SERVIÇO/DESCRIÇÃO são colunas distintas (comando complementar,
// item 14/23-25): "fluid" só reivindica cabeçalhos que nomeiam o Fluido de
// forma inequívoca; a coluna ambígua "Sistema/Fluido / serviço" continua sob
// "service" (e o mapeamento usa "service" como fallback de "fluid" quando o
// arquivo não tiver uma coluna de Fluido própria — nunca o contrário).
//
// DESENHO e FLUXOGRAMA também são campos próprios (item 15/26/27/34):
// "drawing" só reivindica "Desenho"; "flowDiagram" cobre "Fluxograma"/P&ID/
// PFD e também a coluna combinada legada "Fluxograma / desenho" (a massa
// oficial de importação usa esse rótulo único) — o mapeamento usa o valor de
// "flowDiagram" como fallback de "drawing" quando não há "Desenho" dedicado.
const SYSTEM_COLUMNS: readonly ColumnSpec[] = [
  { key: "tag", aliases: ["TAG do sistema", "TAG sistema", "Sistema", "TAG"], required: true },
  { key: "unit", aliases: ["Unidade"] },
  { key: "fluidCode", aliases: ["Cód. fluido", "Codigo do fluido", "Cod fluido"] },
  { key: "fluid", aliases: ["Fluido", "Produto"] },
  { key: "service", aliases: ["Sistema / serviço", "Serviço", "Descrição", "Descricao"] },
  { key: "fluidClass", aliases: ["Classe de fluido", "Classe do fluido"] },
  { key: "nr13Category", aliases: ["Categoria NR-13", "Categoria NR13", "Categoria"] },
  { key: "designPressure", aliases: ["Pressão de projeto (kgf/cm²)", "Pressão de projeto"], defaultUnit: "kgf/cm²" },
  { key: "operatingPressure", aliases: ["Pressão de operação (kgf/cm²)", "Pressão de operação"], defaultUnit: "kgf/cm²" },
  { key: "designTemperature", aliases: ["Temperatura de projeto (°C)", "Temperatura de projeto"], defaultUnit: "°C" },
  { key: "operatingTemperature", aliases: ["Temperatura de operação (°C)", "Temperatura de operação"], defaultUnit: "°C" },
  { key: "pmta", aliases: ["PMTA (kgf/cm²)", "PMTA"], defaultUnit: "kgf/cm²" },
  { key: "pressureClass", aliases: ["Classe de pressão", "Classe de pressao"] },
  { key: "spec", aliases: ["Spec", "Especificação"] },
  { key: "material", aliases: ["Material base", "Material"] },
  { key: "drawing", aliases: ["Desenho de Referência", "Desenho", "Drawing"] },
  // "Desenho / fluxograma" é uma coluna COMBINADA da planilha real
  // ("P&ID-… / ISO-…"). Fica inteira aqui em vez de ser partida por
  // adivinhação: qual lado é desenho e qual é isométrico depende do cliente,
  // e o isométrico não tem campo de primeira classe em `Equipment`.
  { key: "flowDiagram", aliases: ["Fluxograma / desenho", "Desenho / fluxograma", "Fluxograma", "P&ID", "PID", "PFD", "Flow Diagram"] },
  // ISOMÉTRICO é reconhecido (para relatar a divergência ao usuário) mas NÃO
  // vira campo de primeira classe no draft: não existe campo oficial
  // correspondente em `Equipment` hoje (comando "correção da importação",
  // item 8 — não inventar campo de domínio; só extras/preview relatam).
  { key: "isometric", aliases: ["Isométrico", "Isometrico", "Isométrico de inspeção", "Isometrico de inspecao", "ISO"] },
  { key: "pipingCount", aliases: ["Qtd. tubulações", "Quantidade de tubulações"] }
];

const PIPING_COLUMNS: readonly ColumnSpec[] = [
  { key: "systemTag", aliases: ["TAG do sistema", "Sistema"], required: true },
  { key: "tag", aliases: ["TAG da tubulação", "TAG da linha", "TAG"], required: true },
  { key: "unit", aliases: ["Unidade"] },
  { key: "fluidCode", aliases: ["Cód. fluido", "Codigo do fluido"] },
  { key: "fluid", aliases: ["Fluido", "Produto"] },
  { key: "service", aliases: ["Fluido / serviço", "Serviço", "Descrição / serviço", "Descrição", "Descricao"] },
  { key: "lineNumber", aliases: ["Nº linha", "No linha", "Número da linha", "Linha"] },
  // "DE" é exato: ver `exactOnly`. "DE (mm)" é o diâmetro externo, não a origem.
  { key: "from", aliases: ["DE", "Origem"], exactOnly: true },
  { key: "to", aliases: ["PARA", "Destino"] },
  { key: "fluidClass", aliases: ["Classe de fluido", "Cl. fluido", "Cl fluido"] },
  { key: "nr13Category", aliases: ["Categoria NR-13", "Categoria NR13", "Cat. NR-13", "Cat NR13", "Cat"] },
  { key: "nps", aliases: ["Diâmetro nominal (pol)", "Diâmetro nominal", "NPS", "DN", "Diâmetro"] },
  { key: "schedule", aliases: ["SCH", "Schedule"] },
  { key: "nominalThickness", aliases: ["Espessura nominal (mm)", "Espessura nominal", "Esp. nominal", "Esp nominal", "e nom.", "e nom"], defaultUnit: "mm" },
  { key: "minimumThickness", aliases: ["Espessura mínima (mm)", "Espessura mínima", "Esp. mínima", "Esp minima", "e min.", "e min"], defaultUnit: "mm" },
  { key: "designPressure", aliases: ["Pressão de projeto (kgf/cm²)", "Pressão de projeto", "Pressão projeto", "P. proj.", "P proj"], defaultUnit: "kgf/cm²" },
  { key: "operatingPressure", aliases: ["Pressão de operação (kgf/cm²)", "Pressão de operação", "Pressão operação", "P. op.", "P op"], defaultUnit: "kgf/cm²" },
  { key: "designTemperature", aliases: ["Temperatura de projeto (°C)", "Temperatura de projeto", "Temp. projeto", "Temp projeto", "T. proj.", "T proj"], defaultUnit: "°C" },
  { key: "operatingTemperature", aliases: ["Temperatura de operação (°C)", "Temperatura de operação", "Temp. operação", "Temp operação", "T. op.", "T op"], defaultUnit: "°C" },
  { key: "pmta", aliases: ["PMTA (kgf/cm²)", "PMTA"], defaultUnit: "kgf/cm²" },
  { key: "pressureClass", aliases: ["Classe de pressão", "Classe"] },
  { key: "spec", aliases: ["Spec", "Especificação"] },
  // MATERIAL faltava na Lista de Linhas: a coluna existia na planilha e na
  // saída do Book, mas nunca era importada — o material de cada linha tinha
  // de ser redigitado depois, ativo por ativo.
  { key: "material", aliases: ["Material de fabricação", "Material base", "Material"] },
  /**
   * CADASTRO TÉCNICO DA LINHA — campos que existem em `Equipment` e que a
   * planilha traz, mas que a importação deixava cair em `extras`. Sem eles
   * a Lista de Linhas criava o TAG e mais nada: todo o resto era redigitado.
   */
  { key: "outsideDiameter", aliases: ["DE (mm)", "Diâmetro externo", "OD"], defaultUnit: "mm" },
  { key: "corrosionAllowance", aliases: ["CA (mm)", "Sobre-espessura de corrosão", "Sobrespessura"], defaultUnit: "mm" },
  { key: "materialFamily", aliases: ["Família", "Familia", "Família de material"] },
  { key: "fabricationStandard", aliases: ["Norma de fabricação", "Norma fabricação"] },
  { key: "dimensionalStandard", aliases: ["Norma dimensional"] },
  { key: "drawing", aliases: ["Desenho de Referência", "Desenho", "Drawing", "Desenho de ref.", "Desenho ref"] },
  // "Desenho / fluxograma" é uma coluna COMBINADA da planilha real
  // ("P&ID-… / ISO-…"). Fica inteira aqui em vez de ser partida por
  // adivinhação: qual lado é desenho e qual é isométrico depende do cliente,
  // e o isométrico não tem campo de primeira classe em `Equipment`.
  { key: "flowDiagram", aliases: ["Fluxograma / desenho", "Desenho / fluxograma", "Fluxograma", "P&ID", "PID", "PFD", "Flow Diagram"] },
  // ISOMÉTRICO é reconhecido (para relatar a divergência ao usuário) mas NÃO
  // vira campo de primeira classe no draft: não existe campo oficial
  // correspondente em `Equipment` hoje (comando "correção da importação",
  // item 8 — não inventar campo de domínio; só extras/preview relatam).
  { key: "isometric", aliases: ["Isométrico", "Isometrico", "Isométrico de inspeção", "Isometrico de inspecao", "ISO"] }
];

const FLUID_CLASSES = new Set(["A", "B", "C", "D"]);
const NR13_CATEGORIES = new Set(["I", "II", "III", "IV", "V"]);

// -------------------------------------------------------------- mapeamento

function measurement(
  resolver: ColumnResolver,
  row: SpreadsheetRow,
  key: string,
  defaultUnit: string,
  label: string,
  issues: ImportIssue[],
  options?: { allowNegative?: boolean }
): MeasurementDraft | null {
  const header = resolver.header(key);
  if (!header) return null;
  const raw = resolver.text(row, key);
  if (raw === "") return null;
  const value = parseDecimal(raw);
  if (value === null) {
    issues.push({ rowNumber: row.rowNumber, column: header, severity: "ERROR", message: `${label}: "${raw}" não é um número válido.` });
    return null;
  }
  if (value < 0 && !options?.allowNegative) {
    issues.push({ rowNumber: row.rowNumber, column: header, severity: "ERROR", message: `${label} não pode ser negativa (${raw}).` });
    return null;
  }

  // Normalização ANTES de qualquer validação técnica: comparar bar com
  // kgf/cm² produziria divergência falsa. O original é preservado.
  const declaredUnit = unitFromHeader(header, defaultUnit);
  const dimension = FIELD_DIMENSION[key];
  if (!dimension) {
    return { value, unit: declaredUnit, originalValue: value, originalUnit: declaredUnit, recognized: true };
  }
  const normalized = normalizeMeasurement(value, declaredUnit, dimension);
  if (!normalized.recognized) {
    issues.push({
      rowNumber: row.rowNumber, column: header, severity: "WARNING",
      message: `${label}: unidade "${declaredUnit}" não reconhecida. O valor foi mantido como veio e precisa de revisão.`
    });
  }
  return {
    value: normalized.value,
    unit: normalized.unit,
    originalValue: normalized.originalValue,
    originalUnit: normalized.originalUnit,
    recognized: normalized.recognized
  };
}

/**
 * Marcadores de AUSÊNCIA que as planilhas escrevem no lugar de deixar a
 * célula vazia. Importá-los como texto criaria um valor falso: o cadastro
 * passaria a ter o material "NÃO INFORMADO", e o documento imprimiria isso
 * como se fosse o material do ativo.
 *
 * Absorvê-los aqui não perde nada: a camada de apresentação já mostra
 * "NÃO INFORMADO" para um campo ausente (`formatReportValue`), então o
 * documento sai idêntico — a diferença é que o cadastro passa a saber que o
 * dado falta, e a importação seguinte pode preenchê-lo.
 */
const ABSENCE_MARKERS = new Set([
  "nao informado", "n informado", "nao informada", "nao informados", "nao informadas",
  "nao aplicavel", "n a", "na", "n d", "nd", "sem informacao", "s informacao", "-", "--", "---"
]);

function normalizeMarker(value: string): string {
  return value
    .normalize("NFD").replace(/[̀-ͯ]/g, "")
    .replace(/[^a-zA-Z0-9-]+/g, " ")
    .trim().toLowerCase();
}

function isAbsenceMarker(value: string): boolean {
  return ABSENCE_MARKERS.has(normalizeMarker(value));
}

function emptyToNull(value: string): string | null {
  if (value === "") return null;
  return isAbsenceMarker(value) ? null : value;
}

function collectExtras(resolver: ColumnResolver, row: SpreadsheetRow, keys: readonly string[]): Record<string, string> {
  const extras: Record<string, string> = {};
  for (const key of keys) {
    const header = resolver.header(key);
    if (!header) continue;
    const value = resolver.text(row, key);
    if (value !== "") extras[header] = value;
  }
  for (const header of resolver.unmapped) {
    const value = (row.values[header] ?? "").trim();
    if (value !== "") extras[header] = value;
  }
  return extras;
}

function checkDomain(
  resolver: ColumnResolver, row: SpreadsheetRow, key: string, allowed: ReadonlySet<string>, label: string, issues: ImportIssue[]
): string | null {
  const header = resolver.header(key);
  if (!header) return null;
  const raw = resolver.text(row, key);
  if (raw === "") return null;
  if (!allowed.has(raw.toUpperCase())) {
    issues.push({
      rowNumber: row.rowNumber, column: header, severity: "WARNING",
      message: `${label} "${raw}" fora do domínio esperado (${[...allowed].join(", ")}). O valor foi importado como está.`
    });
  }
  return raw;
}

/** Registra TAGs repetidas dentro do próprio arquivo — erro de estrutura. */
function flagDuplicates(drafts: ImportedEquipmentDraft[], issues: ImportIssue[], columnLabel: string): Set<string> {
  const seen = new Map<string, number>();
  const duplicated = new Set<string>();
  for (const draft of drafts) {
    const key = draft.tag.trim().toUpperCase();
    const first = seen.get(key);
    if (first !== undefined) {
      duplicated.add(key);
      issues.push({
        rowNumber: draft.rowNumber, column: columnLabel, severity: "ERROR",
        message: `TAG "${draft.tag}" repetida na planilha (primeira ocorrência na linha ${first}).`
      });
    } else {
      seen.set(key, draft.rowNumber);
    }
  }
  return duplicated;
}

/** Campos que um alias pode apontar, com o título principal de cada um e as abas onde existem. */
export function importTargetFields(): Array<{ field: string; label: string; sheets: string[] }> {
  const byKey = new Map<string, { field: string; label: string; sheets: string[] }>();
  for (const [sheet, specs] of [["Sistemas", SYSTEM_COLUMNS], ["Tubulações", PIPING_COLUMNS]] as const) {
    for (const spec of specs) {
      const current = byKey.get(spec.key) ?? { field: spec.key, label: spec.aliases[0] ?? spec.key, sheets: [] };
      current.sheets.push(sheet);
      byKey.set(spec.key, current);
    }
  }
  return [...byKey.values()].sort((a, b) => a.label.localeCompare(b.label, "pt-BR"));
}

export function mapSystemsSheet(table: SpreadsheetTable, options: ImportMappingOptions = {}): SheetMappingResult {
  const resolver = new ColumnResolver(table.headers, SYSTEM_COLUMNS, options);
  const issues: ImportIssue[] = [];
  const drafts: ImportedEquipmentDraft[] = [];
  const tagHeader = resolver.header("tag") ?? "TAG do sistema";

  for (const row of table.rows) {
    const tag = resolver.text(row, "tag");
    if (tag === "") {
      issues.push({ rowNumber: row.rowNumber, column: tagHeader, severity: "ERROR", message: "TAG do sistema é obrigatória." });
      continue;
    }
    const service = emptyToNull(resolver.text(row, "service"));
    // FLUIDO usa a coluna própria quando existir; sem ela, cai para
    // SERVIÇO/DESCRIÇÃO (arquivo com uma única coluna ambígua) — nunca o
    // inverso, e nunca inferido pela TAG (item 14/23-25).
    const fluid = emptyToNull(resolver.text(row, "fluid")) ?? service;
    // CÓDIGO DO FLUIDO é campo próprio, nunca combinado com FLUIDO: os dois
    // são preservados quando o arquivo os separa (correção da importação,
    // item 5) — sem fallback num sentido ou no outro.
    const fluidCode = emptyToNull(resolver.text(row, "fluidCode"));
    // FLUXOGRAMA usa a coluna própria (ou a combinada legada "Fluxograma /
    // desenho", usada pela massa oficial); DESENHO usa a coluna própria e só
    // cai para o valor de Fluxograma quando não há "Desenho" dedicado —
    // preserva os dois quando o arquivo realmente os separa (item 15/26/27/34).
    const flowDiagramReference = emptyToNull(resolver.text(row, "flowDiagram"));
    const drawingReference = emptyToNull(resolver.text(row, "drawing")) ?? flowDiagramReference;
    const pmta = measurement(resolver, row, "pmta", "kgf/cm²", "PMTA", issues);
    drafts.push({
      rowNumber: row.rowNumber,
      kind: "PIPING_SYSTEM",
      tag,
      name: service ?? tag,
      systemTag: null,
      service,
      fluid,
      fluidCode,
      fluidClass: checkDomain(resolver, row, "fluidClass", FLUID_CLASSES, "Classe de fluido", issues),
      nr13Category: checkDomain(resolver, row, "nr13Category", NR13_CATEGORIES, "Categoria NR-13", issues),
      designPressure: measurement(resolver, row, "designPressure", "kgf/cm²", "Pressão de projeto", issues),
      operatingPressure: measurement(resolver, row, "operatingPressure", "kgf/cm²", "Pressão de operação", issues),
      designTemperature: measurement(resolver, row, "designTemperature", "°C", "Temperatura de projeto", issues, { allowNegative: true }),
      operatingTemperature: measurement(resolver, row, "operatingTemperature", "°C", "Temperatura de operação", issues, { allowNegative: true }),
      pmtaValue: pmta ? pmta.value : null,
      pmtaUnit: pmta ? pmta.unit : null,
      pressureClass: emptyToNull(resolver.text(row, "pressureClass")),
      spec: emptyToNull(resolver.text(row, "spec")),
      material: emptyToNull(resolver.text(row, "material")),
      drawingReference,
      flowDiagramReference,
      lineNumber: null,
      fromReference: null,
      toReference: null,
      nps: null,
      schedule: null,
      nominalThickness: null,
      minimumThickness: null,
      extras: collectExtras(resolver, row, ["unit", "pipingCount", "isometric"])
    });
  }

  flagDuplicates(drafts, issues, tagHeader);
  return { drafts, issues, missingColumns: resolver.missing, unmappedColumns: resolver.unmapped };
}

export function mapPipingSheet(table: SpreadsheetTable, options: ImportMappingOptions = {}): SheetMappingResult {
  const resolver = new ColumnResolver(table.headers, PIPING_COLUMNS, options);
  const issues: ImportIssue[] = [];
  const drafts: ImportedEquipmentDraft[] = [];
  const tagHeader = resolver.header("tag") ?? "TAG da tubulação";
  const systemHeader = resolver.header("systemTag") ?? "TAG do sistema";

  for (const row of table.rows) {
    const tag = resolver.text(row, "tag");
    if (tag === "") {
      issues.push({ rowNumber: row.rowNumber, column: tagHeader, severity: "ERROR", message: "TAG da tubulação é obrigatória." });
      continue;
    }
    const systemTag = resolver.text(row, "systemTag");
    if (systemTag === "") {
      issues.push({
        rowNumber: row.rowNumber, column: systemHeader, severity: "ERROR",
        message: "TAG do sistema é obrigatória: toda tubulação deve pertencer a um Sistema de Tubulação."
      });
      continue;
    }
    const service = emptyToNull(resolver.text(row, "service"));
    const fluid = emptyToNull(resolver.text(row, "fluid")) ?? service;
    const fluidCode = emptyToNull(resolver.text(row, "fluidCode"));
    const flowDiagramReference = emptyToNull(resolver.text(row, "flowDiagram"));
    const drawingReference = emptyToNull(resolver.text(row, "drawing")) ?? flowDiagramReference;
    const pmta = measurement(resolver, row, "pmta", "kgf/cm²", "PMTA", issues);
    drafts.push({
      rowNumber: row.rowNumber,
      kind: "PIPING_LINE",
      tag,
      name: service ? `${tag} — ${service}` : tag,
      systemTag,
      service,
      fluid,
      fluidCode,
      fluidClass: checkDomain(resolver, row, "fluidClass", FLUID_CLASSES, "Classe de fluido", issues),
      nr13Category: checkDomain(resolver, row, "nr13Category", NR13_CATEGORIES, "Categoria NR-13", issues),
      designPressure: measurement(resolver, row, "designPressure", "kgf/cm²", "Pressão de projeto", issues),
      operatingPressure: measurement(resolver, row, "operatingPressure", "kgf/cm²", "Pressão de operação", issues),
      designTemperature: measurement(resolver, row, "designTemperature", "°C", "Temperatura de projeto", issues, { allowNegative: true }),
      operatingTemperature: measurement(resolver, row, "operatingTemperature", "°C", "Temperatura de operação", issues, { allowNegative: true }),
      pmtaValue: pmta ? pmta.value : null,
      pmtaUnit: pmta ? pmta.unit : null,
      pressureClass: emptyToNull(resolver.text(row, "pressureClass")),
      spec: emptyToNull(resolver.text(row, "spec")),
      /**
       * MATERIAL da linha — era `null` fixo.
       *
       * A coluna existe na Lista de Linhas e sai no Book, mas a importação a
       * descartava: mesmo com MATERIAL preenchido na planilha, cada linha
       * chegava sem material e alguém tinha de redigitá-lo ativo por ativo.
       * Nada é inventado aqui — sem coluna ou com célula vazia continua null.
       */
      material: emptyToNull(resolver.text(row, "material")),
      drawingReference,
      flowDiagramReference,
      lineNumber: emptyToNull(resolver.text(row, "lineNumber")),
      fromReference: emptyToNull(resolver.text(row, "from")),
      toReference: emptyToNull(resolver.text(row, "to")),
      nps: emptyToNull(resolver.text(row, "nps")),
      schedule: emptyToNull(resolver.text(row, "schedule")),
      nominalThickness: measurement(resolver, row, "nominalThickness", "mm", "Espessura nominal", issues),
      minimumThickness: measurement(resolver, row, "minimumThickness", "mm", "Espessura mínima", issues),
      // Cadastro técnico: DE, CA, família, norma de fabricação e dimensional.
      outsideDiameter: measurement(resolver, row, "outsideDiameter", "mm", "Diâmetro externo", issues),
      corrosionAllowance: measurement(resolver, row, "corrosionAllowance", "mm", "Sobre-espessura de corrosão", issues),
      materialFamily: emptyToNull(resolver.text(row, "materialFamily")),
      fabricationStandard: emptyToNull(resolver.text(row, "fabricationStandard")),
      dimensionalStandard: emptyToNull(resolver.text(row, "dimensionalStandard")),
      extras: collectExtras(resolver, row, ["unit", "isometric"])
    });
  }

  flagDuplicates(drafts, issues, tagHeader);
  return { drafts, issues, missingColumns: resolver.missing, unmappedColumns: resolver.unmapped };
}

/**
 * Confere o vínculo Tubulação → Sistema contra os Sistemas do próprio arquivo
 * e os já cadastrados. É a mesma regra que `EquipmentService.createEquipment`
 * impõe na gravação; aqui ela aparece antes, na pré-visualização, para o
 * usuário corrigir a planilha sem gravar nada pela metade.
 */
export function validateSystemLinks(
  pipingDrafts: readonly ImportedEquipmentDraft[],
  systemTagsInFile: readonly string[],
  existingSystemTags: readonly string[]
): ImportIssue[] {
  const known = new Set<string>();
  for (const tag of systemTagsInFile) known.add(tag.trim().toUpperCase());
  for (const tag of existingSystemTags) known.add(tag.trim().toUpperCase());

  const issues: ImportIssue[] = [];
  for (const draft of pipingDrafts) {
    const systemTag = (draft.systemTag ?? "").trim();
    if (systemTag === "") continue;
    if (!known.has(systemTag.toUpperCase())) {
      issues.push({
        rowNumber: draft.rowNumber, column: "TAG do sistema", severity: "ERROR",
        message: `Sistema de Tubulação "${systemTag}" não existe na aba Sistemas nem no cadastro do Projeto.`
      });
    }
  }
  return issues;
}

/** Linhas com pelo menos um ERRO — são as que não podem ser gravadas. */
export function rowsBlockedByErrors(issues: readonly ImportIssue[]): Set<number> {
  const blocked = new Set<number>();
  for (const issue of issues) {
    if (issue.severity === "ERROR") blocked.add(issue.rowNumber);
  }
  return blocked;
}

/* ══════════════════════════════════════════════════════════════════════
   MESCLAGEM DE ABAS — a mesma linha descrita em duas tabelas
   ══════════════════════════════════════════════════════════════════════ */

/** A chave que identifica a MESMA linha entre abas: Sistema + TAG. */
export function pipingDraftKey(draft: Pick<ImportedEquipmentDraft, "systemTag" | "tag">): string {
  const system = (draft.systemTag ?? "").trim().toUpperCase();
  const tag = (draft.tag ?? "").trim().toUpperCase();
  return `${system}||${tag}`;
}

/** `null`, `""` e ausência são "sem valor"; qualquer outra coisa preenche. */
function hasValue(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === "string") return value.trim() !== "";
  return true;
}

/**
 * MESCLA os rascunhos da MESMA tubulação vindos de abas diferentes.
 *
 * ────────────────────────────────────────────────────────────────────
 * Por que isto existe.
 * ────────────────────────────────────────────────────────────────────
 *
 * A planilha cadastral real descreve cada tubulação em DUAS tabelas:
 *
 *   1. Dados de processo e identificação  → DE/PARA, NPS, SCH, pressões,
 *                                           temperaturas, PMTA, classe
 *   2. Materiais, dimensões, normas       → DE (mm), espessuras, CA,
 *      e rastreabilidade                    família, material, normas
 *
 * As duas são chaveadas por "TAG da tubulacao". A importação lia UMA aba: a
 * outra era ignorada em silêncio, e metade do cadastro técnico se perdia sem
 * nenhuma mensagem. O usuário via o TAG criado e concluía que a importação
 * "só serve para o TAG".
 *
 * REGRA DA MESCLA: o PRIMEIRO valor preenchido vence. Um campo vazio na
 * segunda aba NUNCA apaga o que a primeira trouxe — foi para não apagar que
 * a mescla existe. Quando as duas abas trazem valores DIFERENTES para o
 * mesmo campo, o primeiro prevalece e a divergência é relatada como AVISO:
 * escolher sozinho e não contar seria decidir um dado técnico no escuro.
 *
 * `extras` são unidos: colunas sem campo de domínio de ambas as abas.
 */
export function mergePipingDrafts(
  drafts: readonly ImportedEquipmentDraft[]
): { drafts: ImportedEquipmentDraft[]; issues: ImportIssue[] } {
  const byKey = new Map<string, ImportedEquipmentDraft>();
  const order: string[] = [];
  const issues: ImportIssue[] = [];

  for (const draft of drafts) {
    const key = pipingDraftKey(draft);
    const existing = byKey.get(key);
    if (!existing) {
      byKey.set(key, draft);
      order.push(key);
      continue;
    }

    const merged: Record<string, unknown> = { ...existing };
    for (const [field, incoming] of Object.entries(draft)) {
      if (field === "extras" || field === "rowNumber") continue;
      const current = (existing as unknown as Record<string, unknown>)[field];

      if (!hasValue(current)) {
        if (hasValue(incoming)) merged[field] = incoming;
        continue;
      }
      if (!hasValue(incoming)) continue;

      // Ambas preencheram. Iguais: nada a fazer. Diferentes: relatar.
      const same = typeof current === "object" && typeof incoming === "object"
        ? JSON.stringify(current) === JSON.stringify(incoming)
        : String(current) === String(incoming);
      if (!same) {
        issues.push({
          rowNumber: draft.rowNumber,
          column: field,
          severity: "WARNING",
          message: `"${existing.tag}" aparece em mais de uma aba com valores diferentes para "${field}". Manteve-se o primeiro ("${JSON.stringify(current)}"); o outro era "${JSON.stringify(incoming)}". Confira a planilha.`
        });
      }
    }
    merged["extras"] = { ...existing.extras, ...draft.extras };
    byKey.set(key, merged as unknown as ImportedEquipmentDraft);
  }

  return { drafts: order.map(key => byKey.get(key)!), issues };
}
